import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, ShieldCheck, Wrench } from "lucide-react";
import { Badge, type BadgeProps } from "@/components/Badge";
import { Button } from "@/components/Button";
import { Card } from "@/components/Card";
import { Fehlerzeile } from "@/components/Fehlerzeile";
import { Label } from "@/components/Label";
import { Leerzustand } from "@/components/Leerzustand";
import { ModellAuswahl } from "@/components/ModellAuswahl";
import { api } from "@/lib/api";
import { fehlerMeldung, useProjekte } from "@/lib/haken";
import {
  einzelnesProjekt,
  useProjektAuswahl,
} from "@/lib/projekt-auswahl";
import type { Meldung } from "@/lib/meldung";

type ModellAuswahl = { verbindungId: string; modell: string };

const modellkennungMuster = /^[a-zA-Z0-9._:/-]{1,200}$/;
const aktiveZustaende = new Set(["wartet", "laeuft"]);

function statusDarstellung(zustand: string): {
  text: string;
  variant: BadgeProps["variant"];
} {
  switch (zustand) {
    case "wartet":
      return { text: "Wartet", variant: "neutral" };
    case "laeuft":
      return { text: "Prüft …", variant: "default" };
    case "fertig":
      return { text: "Abgeschlossen", variant: "success" };
    case "gescheitert":
      return { text: "Fehlgeschlagen", variant: "error" };
    default:
      return { text: zustand, variant: "neutral" };
  }
}

function schwereVariante(
  schwere: string,
): BadgeProps["variant"] {
  if (schwere === "fehler") return "error";
  if (schwere === "warnung") return "warning";
  return "neutral";
}

export function HausmeisterAbschnitt() {
  const client = useQueryClient();
  const { auswahl } = useProjektAuswahl();
  const projektId = einzelnesProjekt(auswahl);
  const projekte = useProjekte();
  const [modellAuswahl, setzeModellAuswahl] =
    useState<ModellAuswahl | null>(null);
  const [meldung, setzeMeldung] = useState<Meldung | null>(null);
  const [gestartet, setzeGestartet] = useState(false);

  const verbindungen = useQuery({
    queryKey: ["cubic", "metarr", "anbieter-verbindungen"],
    queryFn: () => api.metarr.anbieterVerbindungen.query(),
  });
  const konfiguration = useQuery({
    queryKey: ["cubic", "metarr", "modell-konfiguration"],
    queryFn: () => api.metarr.modellKonfiguration.query(),
  });
  const laeufe = useQuery({
    queryKey: ["cubic", "metarr", "hausmeister-laeufe"],
    queryFn: () => api.metarr.hausmeisterLaeufe.query(),
    refetchInterval: (abfrage) =>
      abfrage.state.data?.some((lauf) => aktiveZustaende.has(lauf.zustand))
        ? 1_000
        : false,
  });

  const ersteVerbindung = verbindungen.data?.verbindungen[0];
  const aktuelleAuswahl =
    modellAuswahl ??
    konfiguration.data?.zuordnung.hausmeister ??
    (ersteVerbindung
      ? {
          verbindungId: ersteVerbindung.id,
          modell: ersteVerbindung.modelle[0] ?? "",
        }
      : null);
  const aktiverLauf = laeufe.data?.some((lauf) =>
    aktiveZustaende.has(lauf.zustand),
  );
  const projektName = projektId
    ? (projekte.data?.find((projekt) => projekt.id === projektId)?.name ??
      "Ausgewähltes Projekt")
    : "Alle sichtbaren Projekte";

  const starten = useMutation({
    mutationFn: () =>
      api.metarr.hausmeister.mutate({
        ...aktuelleAuswahl!,
        ...(projektId ? { projektId } : {}),
      }),
    onSuccess: async () => {
      setzeMeldung(null);
      setzeGestartet(true);
      await client.invalidateQueries({ queryKey: ["cubic", "metarr"] });
    },
    onError: (fehler) => {
      setzeGestartet(false);
      setzeMeldung(fehlerMeldung(fehler));
    },
  });

  return (
    <Card className="mb-8" data-testid="hausmeister-abschnitt">
      <div className="flex flex-col gap-5">
        <div className="flex items-start gap-3">
          <span className="rounded-lg bg-blue-50 p-2 text-blue-600 dark:bg-blue-950/50 dark:text-blue-300">
            <Wrench className="size-5" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <h2 className="font-semibold text-gray-950 dark:text-gray-50">
              Hausmeister
            </h2>
            <p className="mt-1 max-w-3xl text-sm leading-6 text-gray-600 dark:text-gray-400">
              Der Hausmeister prüft Projekte lesend auf fehlende oder
              widersprüchliche Verknüpfungen. Er ändert nichts selbst. Sein
              Bericht erscheint hier; umsetzbare Änderungen landen als
              Vorschläge und müssen von dir bestätigt werden.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 rounded-lg bg-gray-50 px-3 py-2.5 text-sm dark:bg-gray-900">
          <ShieldCheck className="size-4 text-emerald-600" aria-hidden="true" />
          <span className="font-medium text-gray-900 dark:text-gray-100">
            Prüfbereich: {projektName}
          </span>
          <span className="text-gray-500">
            Über die Projektauswahl oben links ändern.
          </span>
        </div>

        {meldung ? <Fehlerzeile meldung={meldung} /> : null}

        {aktuelleAuswahl && verbindungen.data ? (
          <div className="grid gap-3 md:grid-cols-[minmax(180px,0.8fr)_minmax(220px,1fr)_auto] md:items-end">
            <div>
              <Label htmlFor="hausmeister-anbieter">Anbieter</Label>
              <select
                id="hausmeister-anbieter"
                value={aktuelleAuswahl.verbindungId}
                onChange={(ereignis) => {
                  const verbindung = verbindungen.data!.verbindungen.find(
                    (eintrag) => eintrag.id === ereignis.target.value,
                  );
                  if (!verbindung) return;
                  setzeGestartet(false);
                  setzeModellAuswahl({
                    verbindungId: verbindung.id,
                    modell: verbindung.modelle[0] ?? "",
                  });
                }}
                className="mt-1.5 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 shadow-xs outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:border-gray-800 dark:bg-gray-925 dark:text-gray-50"
              >
                {verbindungen.data.verbindungen.map((verbindung) => (
                  <option key={verbindung.id} value={verbindung.id}>
                    {verbindung.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <Label htmlFor="hausmeister-modell">Modell</Label>
              <ModellAuswahl
                id="hausmeister-modell"
                className="mt-1.5"
                modelle={
                  verbindungen.data.verbindungen.find(
                    (verbindung) =>
                      verbindung.id === aktuelleAuswahl.verbindungId,
                  )?.modelle ?? []
                }
                wert={aktuelleAuswahl.modell}
                onChange={(modell) => {
                  setzeGestartet(false);
                  setzeModellAuswahl({
                    ...aktuelleAuswahl,
                    modell,
                  });
                }}
              />
            </div>
            <Button
              onClick={() => starten.mutate()}
              disabled={
                Boolean(aktiverLauf) ||
                !modellkennungMuster.test(aktuelleAuswahl.modell)
              }
              isLoading={starten.isPending}
              loadingText="Starte Prüfung …"
              data-testid="metarr-hausmeister"
            >
              Prüfung starten
            </Button>
          </div>
        ) : (
          <p className="rounded-md border border-dashed border-gray-300 p-3 text-sm text-gray-500 dark:border-gray-700">
            Verknüpfe oben zuerst einen KI-Anbieter. Danach kann der
            Hausmeister gestartet werden.
          </p>
        )}

        {gestartet ? (
          <p className="text-sm text-emerald-700 dark:text-emerald-400" role="status">
            Die Prüfung wurde gestartet. Der Bericht aktualisiert sich
            automatisch.
          </p>
        ) : null}

        <div className="border-t border-gray-200 pt-5 dark:border-gray-800">
          <h3 className="mb-3 text-sm font-semibold text-gray-900 dark:text-gray-50">
            Letzte Prüfungen
          </h3>
          {laeufe.isLoading ? (
            <p className="text-sm text-gray-500">Lade Prüfungen …</p>
          ) : laeufe.isError ? (
            <Fehlerzeile
              meldung={fehlerMeldung(laeufe.error)}
              onErneut={() => void laeufe.refetch()}
            />
          ) : (laeufe.data?.length ?? 0) === 0 ? (
            <Leerzustand
              titel="Noch keine Prüfung."
              hinweis="Starte den Hausmeister, um Verknüpfungen und Strukturen zu prüfen."
            />
          ) : (
            <ul className="space-y-3" data-testid="hausmeister-laeufe">
              {laeufe.data!.slice(0, 3).map((lauf) => {
                const status = statusDarstellung(lauf.zustand);
                return (
                  <li
                    key={lauf.id}
                    className="rounded-lg border border-gray-200 p-4 dark:border-gray-800"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-50">
                          {lauf.projektId
                            ? (projekte.data?.find(
                                (projekt) => projekt.id === lauf.projektId,
                              )?.name ?? "Projektprüfung")
                            : "Globale Prüfung"}
                        </p>
                        <p className="mt-0.5 text-xs text-gray-500">
                          {new Date(lauf.angelegtAm).toLocaleString("de-DE", {
                            dateStyle: "short",
                            timeStyle: "short",
                          })}
                          {lauf.modell ? ` · ${lauf.modell}` : ""}
                          {lauf.dauerMs != null
                            ? ` · ${lauf.dauerMs} ms`
                            : ""}
                        </p>
                      </div>
                      <Badge variant={status.variant}>{status.text}</Badge>
                    </div>

                    {lauf.letzterFehler ? (
                      <p
                        className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-800 dark:bg-red-950/40 dark:text-red-200"
                        role="alert"
                      >
                        {lauf.letzterFehler}
                      </p>
                    ) : null}

                    {lauf.zustand === "fertig" ? (
                      <div className="mt-3 space-y-3">
                        <div>
                          <p className="text-sm text-gray-700 dark:text-gray-300">
                            {lauf.bericht.zusammenfassung}
                          </p>
                          <p className="mt-1 text-xs text-gray-500">
                            {lauf.bericht.gepruefteObjekte} Objekte geprüft ·{" "}
                            {lauf.bericht.auffaelligkeiten.length}{" "}
                            Auffälligkeiten
                            {!lauf.bericht.kontextVollstaendig
                              ? " · begrenzter Auszug"
                              : ""}
                          </p>
                        </div>
                        {lauf.bericht.auffaelligkeiten.length === 0 &&
                        lauf.bericht.kontextVollstaendig ? (
                          <div className="flex items-center gap-2 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200">
                            <CheckCircle2 className="size-4" aria-hidden="true" />
                            Keine Auffälligkeiten im geprüften Auszug gefunden.
                          </div>
                        ) : lauf.bericht.auffaelligkeiten.length === 0 ? (
                          <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-950/40 dark:text-amber-200">
                            Dieser ältere oder begrenzte Lauf enthält keinen
                            vollständig prüfbaren Bericht.
                          </p>
                        ) : (
                          <ul className="space-y-2">
                            {lauf.bericht.auffaelligkeiten.map(
                              (auffaelligkeit, index) => (
                                <li
                                  key={`${lauf.id}-${index}`}
                                  className="rounded-md bg-gray-50 px-3 py-2 dark:bg-gray-900"
                                >
                                  <div className="flex flex-wrap items-center gap-2">
                                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                      {auffaelligkeit.titel}
                                    </p>
                                    <Badge
                                      variant={schwereVariante(
                                        auffaelligkeit.schwere,
                                      )}
                                    >
                                      {auffaelligkeit.schwere}
                                    </Badge>
                                  </div>
                                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                                    {auffaelligkeit.beschreibung}
                                  </p>
                                </li>
                              ),
                            )}
                          </ul>
                        )}
                        {lauf.vorschlagAnzahl > 0 ? (
                          <a
                            href="#metarr-vorschlaege"
                            className="inline-flex text-sm font-medium text-blue-600 hover:underline dark:text-blue-400"
                          >
                            {lauf.vorschlagAnzahl} Vorschlag
                            {lauf.vorschlagAnzahl === 1 ? "" : "e"} ansehen
                          </a>
                        ) : null}
                      </div>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </Card>
  );
}
